import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Gem, Ship, Landmark, Sparkles, ShieldCheck, Globe2 } from "lucide-react";
import ThreeRing from "../components/ThreeRing";
import GoldParticles from "../components/GoldParticles";
import StatCounter from "../components/StatCounter";
import Reveal from "../components/Reveal";
import MagneticButton from "../components/MagneticButton";
import SectionHeading from "../components/SectionHeading";
import TestimonialCard from "../components/TestimonialCard";
import InstagramReels from "../components/InstagramReels";
import { STATS, TESTIMONIALS, SERVICES, waLink, HERO_IMAGES } from "../lib/constants";

const CATEGORIES = [
  { name: "Rings", img: "https://images.unsplash.com/photo-1727726225026-7730bbcd5062?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxnb2xkJTIwcmluZyUyMGJsYWNrJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3NzgwNDE1OTB8MA&ixlib=rb-4.1.0&q=85", count: "1,200+ designs" },
  { name: "Pendants", img: "https://images.unsplash.com/photo-1735480166632-38176b26f212?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxkaWFtb25kJTIwbmVja2xhY2UlMjBkYXJrJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3NzgwNDE1OTB8MA&ixlib=rb-4.1.0&q=85", count: "1,600+ designs" },
  { name: "Earrings", img: "https://images.unsplash.com/photo-1742137188729-d46186ac5733?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBqZXdlbHJ5JTIwZmxhdGxheSUyMGRhcmt8ZW58MHx8fHwxNzc4MDQxNTkwfDA&ixlib=rb-4.1.0&q=85", count: "900+ designs" },
];

const SERVICE_ICONS = {
  sourcing: Gem,
  recommendations: Sparkles,
  logistics: Ship,
  financing: Landmark,
  certification: ShieldCheck,
};

const BADGES = ["BIS Hallmarked", "GIA Certified", "IGI Verified", "GJEPC Member", "MSME Registered"];

function Hero() {
  return (
    <section data-testid="home-hero" className="relative min-h-screen flex items-center overflow-hidden grain">
      <div className="absolute inset-0 bg-gradient-to-b from-[#FAF6F0] via-[#FAF6F0] to-[#E8DDD0]/40" />
      <GoldParticles count={55} />
      <div className="relative z-10 max-w-[1400px] w-full mx-auto px-6 lg:px-12 pt-28 pb-16 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
        <div className="lg:col-span-7 animate-fade-up">
          <div className="caption text-[#B5896E] mb-6 flex items-center gap-3">
            <span className="inline-block w-8 h-[1px] bg-[#B5896E]" />
            Transforming Jewelry Sourcing For You
          </div>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.98] text-[#4A3F35]">
            Where India's
            <br />
            <span className="italic text-[#B5896E]">Finest Jewelry</span>
            <br />
            Meets the World
          </h1>
          <p className="mt-8 max-w-xl text-base lg:text-lg text-[#4A3F35]/70 leading-relaxed">
            A B2B sourcing platform connecting jewelry retailers to 5,000+ designs,
            certified manufacturers, and end-to-end logistics — engineered for the
            independent jeweler going global.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-5">
            <MagneticButton testId="hero-cta-browse" variant="primary" href={waLink("Hi Blue Velvet, please share your latest catalogue. I'd like to see your collection.")}>
              Request Catalogue
              <ArrowUpRight size={16} className="ml-2" />
            </MagneticButton>
            <Link to="/retailers">
              <MagneticButton testId="hero-cta-partner" variant="outline">Partner With Us</MagneticButton>
            </Link>
          </div>
        </div>
        <div className="lg:col-span-5 h-[420px] lg:h-[560px] relative">
          <div className="absolute inset-0"><ThreeRing /></div>
          <div className="absolute inset-0 pointer-events-none rounded-full" style={{ background: "radial-gradient(circle at center, rgba(201,168,76,0.12) 0%, transparent 60%)" }} />
        </div>
      </div>
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 caption text-[#4A3F35]/50">Scroll to explore ↓</div>
    </section>
  );
}

function Stats() {
  return (
    <section data-testid="home-stats" className="relative py-24 bg-[#E8DDD0]/20 border-y border-[#B5896E]/10">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 80}>
              <StatCounter testId={`stat-${i}`} {...s} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function BadgesMarquee() {
  return (
    <section className="py-10 border-b border-[#B5896E]/10 overflow-hidden">
      <div className="flex gap-16 whitespace-nowrap marquee">
        {[...BADGES, ...BADGES, ...BADGES].map((b, i) => (
          <span key={`${b}-${i}`} className="caption text-[#4A3F35]/50 flex items-center gap-3">
            <span className="inline-block w-1.5 h-1.5 bg-[#B5896E] rounded-full" /> {b}
          </span>
        ))}
      </div>
    </section>
  );
}

function CategoryCard({ category, delay }) {
  return (
    <Reveal delay={delay}>
      <a
        href={waLink(`Hi Blue Velvet, I'd like to see your ${category.name} collection.`)}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`home-category-${category.name.toLowerCase()}`}
        className="group block tilt-card gold-border bg-[#FFFFFF]/40 overflow-hidden"
      >
        <div className="relative aspect-[3/4] overflow-hidden">
          <img src={category.img} alt={category.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-110" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#FAF6F0] via-[#FAF6F0]/30 to-transparent" />
          <div className="absolute inset-0 p-8 flex flex-col justify-end">
            <h3 className="font-display text-3xl text-[#4A3F35]">{category.name}</h3>
            <div className="mt-2 caption text-[#B5896E]">{category.count}</div>
          </div>
        </div>
      </a>
    </Reveal>
  );
}

function Categories() {
  return (
    <section data-testid="home-categories" className="py-24 lg:py-32">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="flex items-end justify-between gap-8 mb-16 flex-wrap">
          <Reveal>
            <SectionHeading eyebrow="The Collection" title={<>Categories engineered for <em className="italic text-[#B5896E]">your shelf</em></>} />
          </Reveal>
          <Reveal delay={120}>
            <a href={waLink("Hi Blue Velvet, please share your latest catalogue.")} target="_blank" rel="noopener noreferrer" data-testid="home-cat-view-all" className="caption text-[#B5896E] inline-flex items-center gap-2 underline-gold">
              Request full catalogue <ArrowUpRight size={14} />
            </a>
          </Reveal>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {CATEGORIES.map((c, i) => <CategoryCard key={c.name} category={c} delay={i * 100} />)}
        </div>
      </div>
    </section>
  );
}

function ServiceCard({ service, delay }) {
  const Icon = SERVICE_ICONS[service.key] || Gem;
  return (
    <Reveal delay={delay}>
      <div className="p-8 gold-border bg-[#FFFFFF]/50 h-full hover:border-[#B5896E]/60 transition-colors">
        <Icon size={28} className="text-[#B5896E] mb-6" />
        <h4 className="font-display text-xl mb-2">{service.title}</h4>
        <p className="text-sm text-[#4A3F35]/60 leading-relaxed">{service.blurb}</p>
      </div>
    </Reveal>
  );
}

function ServicesStrip() {
  return (
    <section data-testid="home-services" className="py-24 lg:py-32 bg-[#E8DDD0]/25 border-y border-[#B5896E]/10">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <Reveal>
          <SectionHeading
            eyebrow="One Platform"
            title={<>Every service your jewelry business <em className="italic text-[#B5896E]">needs</em></>}
            subtitle="From the atelier to your storefront — sourcing, logistics, financing and certification under one roof."
          />
        </Reveal>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          {SERVICES.map((s, i) => <ServiceCard key={s.key} service={s} delay={i * 80} />)}
        </div>
        <div className="mt-12">
          <MagneticButton testId="home-services-cta" variant="outline" href={waLink("Hi Blue Velvet, I'd like to learn more about your services.")}>
            Talk to us about services
          </MagneticButton>
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section data-testid="home-testimonials" className="py-24 lg:py-32">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <Reveal>
          <SectionHeading eyebrow="Trusted By" title={<>Independent jewelers, scaling <em className="italic text-[#B5896E]">like national chains</em></>} />
        </Reveal>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-8">
          {TESTIMONIALS.map((t, i) => (
            <Reveal key={t.name} delay={i * 120}>
              <TestimonialCard testId={`testimonial-${i}`} {...t} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function GlobalBanner() {
  return (
    <section data-testid="home-global-banner" className="relative py-24 lg:py-32 overflow-hidden">
      <div className="absolute inset-0">
        <img src={HERO_IMAGES.retailersBanner} alt="" className="w-full h-full object-cover opacity-25" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#FAF6F0] via-[#FAF6F0]/85 to-[#E8DDD0]/60" />
      </div>
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <Reveal>
            <div className="caption text-[#B5896E] mb-5 flex items-center gap-3"><Globe2 size={14} /> For Global Retailers</div>
            <h2 className="font-display text-4xl lg:text-5xl leading-tight">
              Singapore. Malaysia. UAE. UK. USA.<br />
              <em className="italic text-[#B5896E]">Indian craftsmanship, sourced the modern way.</em>
            </h2>
            <p className="mt-6 text-base text-[#4A3F35]/70 max-w-xl">
              Price advantage of Indian manufacturing. Quality of certified ateliers. Logistics, customs and financing — handled. No MOQ barriers to begin.
            </p>
          </Reveal>
        </div>
        <div className="lg:col-span-5 flex flex-col gap-4 lg:items-end">
          <MagneticButton testId="home-global-whatsapp" variant="primary" href={waLink("Hi Blue Velvet, I'm a retailer from [country]. I'd like to explore partnership.")}>
            Chat on WhatsApp
          </MagneticButton>
          <Link to="/retailers">
            <MagneticButton testId="home-global-retailers" variant="outline">Partner details</MagneticButton>
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Hero />
      <Stats />
      <BadgesMarquee />
      {/* <Categories /> */}
      <ServicesStrip />
      <Testimonials />
      <InstagramReels />
      <GlobalBanner />
    </>
  );
}
